/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {
  Component,
  ElementRef,
  HostListener,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {IxModule} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';

import {ContentLanguageSelectionService} from '../../../../../services/content-language-selection.service';
import {resolveEffectiveLanguage} from '../../utils/effective-language.resolver';
import {flagAssetPath} from '../../utils/flag-asset.util';
import {languageDisplayName} from '../../utils/language-display-name.util';

/**
 * Minimal shape a Language-Filterable Attribute entry must have for the
 * picker to resolve and display its Effective Language. Extra fields (e.g.
 * a document's `title`/`url`) are read by the parent `app-viewer-field`
 * dispatch case, never by this component.
 */
export type LanguageEntry = {languageCode?: string; language?: string};

/**
 * Reads the language code of a Language-Filterable Attribute entry.
 * Schemas name the field either `languageCode` or `language`; `languageCode`
 * wins when both are present.
 *
 * @param entry - The attribute entry, possibly null or undefined.
 * @returns The entry's language code, or `undefined` when it has none.
 */
export function languageCodeFor(
  entry: LanguageEntry | null | undefined,
): string | undefined {
  return entry?.languageCode ?? entry?.language;
}

let instanceCounter = 0;

/**
 * Badge showing the Effective Language of a Language-Filterable Attribute
 * instance (a multi-language document row or plain-text attribute). When the
 * instance has more than one available language, the badge opens a popover
 * menu (search, "Apply to all" checkbox, language list) letting the Viewer
 * switch just that one instance to a different language.
 *
 * One instance of this component corresponds to one instance of such an
 * attribute; it is reused unmodified for every occurrence in the Viewer.
 * Renders an iX `ix-button` (a native button inside its shadow DOM),
 * `disabled` when the instance has only one available language, and nothing
 * at all when the instance has no usable language entry.
 */
@Component({
  selector: 'app-language-picker',
  exportAs: 'appLanguagePicker',
  imports: [IxModule],
  templateUrl: './language-picker.component.html',
  styleUrl: './language-picker.component.scss',
})
export class LanguagePickerComponent {
  private readonly contentLanguage = inject(ContentLanguageSelectionService);
  private readonly translate = inject(TranslateService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly languageEntries = input.required<LanguageEntry[]>();
  readonly fullValuePath = input.required<string>();
  readonly attributeLabel = input.required<string>();

  @ViewChild('searchInput')
  private readonly searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChildren('optionRef')
  private readonly optionRefs?: QueryList<ElementRef<HTMLLIElement>>;

  private readonly instanceId: number;
  readonly badgeId: string;
  readonly searchInputId: string;
  readonly listboxId: string;
  readonly applyToAllId: string;

  /**
   * Assigns a per-instance counter so the badge, search input, listbox and
   * checkbox get DOM ids that are unique on a page with many pickers.
   */
  constructor() {
    instanceCounter++;
    this.instanceId = instanceCounter;
    this.badgeId = `language-picker-badge-${this.instanceId}`;
    this.searchInputId = `language-picker-search-${this.instanceId}`;
    this.listboxId = `language-picker-listbox-${this.instanceId}`;
    this.applyToAllId = `language-picker-apply-all-${this.instanceId}`;
  }

  /**
   * The instance's usable language entries in data order: entries with a
   * missing, blank, padded or repeated language code are dropped.
   */
  readonly availableEntries = computed(() => {
    const seen = new Set<string>();
    const entries: {languageCode: string}[] = [];
    for (const entry of this.languageEntries()) {
      const code = languageCodeFor(entry);
      if (
        typeof code === 'string' &&
        code.trim() &&
        code === code.trim() &&
        !seen.has(code)
      ) {
        seen.add(code);
        entries.push({languageCode: code});
      }
    }
    return entries;
  });
  /** True when the instance has at least one usable language entry. */
  readonly hasLanguages = computed(() => this.availableEntries().length > 0);
  readonly isOpen = signal(false);
  readonly searchQuery = signal('');
  readonly applyToAll = signal(false);
  /** Index of the keyboard-active option; -1 while focus is still in the search field. */
  readonly activeIndex = signal(-1);
  readonly announcement = signal('');

  /** Display name of the Effective Language, or its uppercase code when no name is known. */
  readonly badgeText = computed(() => {
    const languageCode = this.effectiveLanguage();
    return (
      this.displayNameFor(languageCode) || languageCode?.toUpperCase() || ''
    );
  });

  /** The language currently displayed for this instance (Override → Selected → English → first entry). */
  readonly effectiveLanguage = computed(() =>
    resolveEffectiveLanguage(
      this.availableEntries().map((entry) => entry.languageCode),
      this.contentLanguage.overrides().get(this.fullValuePath()) ?? null,
      this.contentLanguage.selectedLanguage(),
    ),
  );

  /** Local flag asset path for the Effective Language, or null when outside the allowlist. */
  readonly flagIconPath = computed(() =>
    flagAssetPath(this.effectiveLanguage()),
  );

  /** True when there is at most one available language, in which case the badge is non-interactive. */
  readonly isSingleLanguage = computed(
    () => this.availableEntries().length <= 1,
  );

  /** Option that is reachable with Tab: the active one, or the first while none is active. */
  readonly tabbableIndex = computed(() => Math.max(this.activeIndex(), 0));

  /** Accessible name stating both the attribute and its currently displayed language. */
  readonly accessibleName = computed(() => {
    return `${this.attributeLabel()}, ${this.badgeText()}`;
  });

  /** Available languages for this instance whose code or display name matches the current search query. */
  readonly filteredEntries = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return this.availableEntries();
    }
    return this.availableEntries().filter(
      (entry) =>
        entry.languageCode.toLowerCase().includes(query) ||
        this.displayNameFor(entry.languageCode).toLowerCase().includes(query),
    );
  });

  /**
   * Returns the language's name in the current UI language via
   * `Intl.DisplayNames` (for example `de` -> "German" in an English UI).
   *
   * @param languageCode - Language code to name (for example: en, de).
   * @returns The display name, the uppercase code if no name is available, or
   *   an empty string when no code is given.
   */
  displayNameFor(languageCode: string | undefined): string {
    return languageDisplayName(
      languageCode,
      this.translate.currentLang || this.translate.defaultLang,
    );
  }

  /** Local flag asset path for the given language code, or null when outside the allowlist. */
  flagPathFor(languageCode: string): string | null {
    return flagAssetPath(languageCode);
  }

  /** Opens or closes the language menu; does nothing when there is no other language to switch to. */
  toggleMenu(): void {
    if (!this.hasLanguages() || this.isSingleLanguage()) {
      return;
    }
    if (this.isOpen()) {
      this.closeMenu(false);
    } else {
      this.openMenu();
    }
  }

  private openMenu(): void {
    this.searchQuery.set('');
    this.activeIndex.set(-1);
    // Every opening starts as a single-instance switch, so a pick never
    // switches the whole page because of a choice made in an earlier opening.
    this.applyToAll.set(false);
    this.isOpen.set(true);
    queueMicrotask(() => this.searchInputRef?.nativeElement.focus());
  }

  /**
   * Closes the language menu.
   *
   * @param refocusBadge - When true, moves focus back to the badge button
   *   (used after a pick or `Escape`, but not after an outside click).
   */
  closeMenu(refocusBadge: boolean): void {
    this.isOpen.set(false);
    if (refocusBadge) {
      queueMicrotask(() => {
        this.elementRef.nativeElement.ownerDocument
          .getElementById(this.badgeId)
          ?.focus();
      });
    }
  }

  /**
   * Filters the language list and resets keyboard navigation to the first match.
   *
   * @param value - The current search input text.
   */
  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.activeIndex.set(-1);
  }

  /**
   * Stores the "Apply to all" state, which decides whether the next pick
   * switches the whole page or only this instance, and announces the change.
   * The state is cleared again every time the menu opens.
   *
   * @param checked - The new checkbox state.
   */
  onApplyToAllChange(checked: boolean): void {
    this.applyToAll.set(checked);
    this.announcement.set(
      checked
        ? 'Apply to all languages enabled'
        : 'Apply to all languages disabled',
    );
  }

  /**
   * Applies a picked language: with "Apply to all" checked it sets the
   * page-wide Selected Language and clears every Instance Override
   * (`switchAll`); otherwise it sets an Instance Override for this instance
   * only. Announces the change and closes the menu.
   *
   * @param languageCode - The picked language code.
   */
  pickLanguage(languageCode: string): void {
    if (this.applyToAll()) {
      this.contentLanguage.switchAll(languageCode);
      this.announcement.set(
        `All documents and attributes switched to ${this.displayNameFor(languageCode)}`,
      );
    } else {
      this.contentLanguage.setInstanceOverride(
        this.fullValuePath(),
        languageCode,
      );
      this.announcement.set(
        `${this.attributeLabel()} switched to ${this.displayNameFor(languageCode)}`,
      );
    }
    this.closeMenu(true);
  }

  /**
   * Keyboard handling inside the open menu (search field, checkbox and
   * options): `Escape` closes it and returns focus to the badge;
   * `ArrowDown`/`ArrowUp` move through the filtered list, wrapping at both
   * ends. The first `ArrowDown` from the search field selects the first option.
   *
   * @param event - The keyboard event raised inside the menu.
   */
  onMenuKeydown(event: KeyboardEvent): void {
    const optionCount = this.filteredEntries().length;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu(true);
      return;
    }
    if (event.key === 'ArrowDown' && optionCount > 0) {
      event.preventDefault();
      this.activeIndex.set((this.activeIndex() + 1) % optionCount);
      this.focusActiveOption();
      return;
    }
    if (event.key === 'ArrowUp' && optionCount > 0) {
      event.preventDefault();
      const current = this.activeIndex();
      this.activeIndex.set(current <= 0 ? optionCount - 1 : current - 1);
      this.focusActiveOption();
    }
  }

  private focusActiveOption(): void {
    queueMicrotask(() =>
      this.optionRefs?.get(this.activeIndex())?.nativeElement.focus(),
    );
  }

  /**
   * Closes the open menu when the user clicks anywhere outside this component.
   *
   * @param event - The document click event.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.isOpen() &&
      !this.elementRef.nativeElement.contains(event.target as Node)
    ) {
      this.closeMenu(false);
    }
  }
}
